import { MutateOptions, UseMutationResult, useMutation } from 'react-query';
import { destroyAlert, errorAlert, loadingAlert, successAlert } from '../../../utils/alert';
import { getDownloadResult, putPedlaboratorioResultado, reprocessarOs } from '..';

//#region BAIXAR ANÁLISE (RESULTADO PROCESSADO)
interface ParamsDownloadResultado {
    arquivo_processado_url: string
    cod: number
}

export const useDownloadResultado = (): {
    carregando_download: boolean;
    mutate_download: (params: ParamsDownloadResultado) => void;
} => {

    const mutation: UseMutationResult<void, Error, ParamsDownloadResultado> = useMutation(
        async (params: ParamsDownloadResultado) => {
            const { arquivo_processado_url, cod } = params;

            if (!arquivo_processado_url) {
                errorAlert('Não encontramos arquivos processados. Faça o upload de um arquivo para prosseguir', 5000);
                return;
            }

            loadingAlert({ msg: 'Baixando análise...', id: 'download_os' });

            return getDownloadResult(arquivo_processado_url)
                .then((response: any) => {
                    const url = URL.createObjectURL(response.data);
                    const link = document.createElement('a');
                    link.href = url;
                    const nomeArquivo = arquivo_processado_url.split('/').pop()?.split('?')[0]?.split('#')[0];
                    link.download = nomeArquivo || `analise_${cod}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                    destroyAlert('download_os');
                })
                .catch((error: any) => {
                    errorAlert('Falha ao baixar a análise.', 5000, 'download_os');
                    throw error;
                });
        }
    );

    return {
        carregando_download: mutation.isLoading,
        mutate_download: mutation.mutate,
    };
};
//#endregion

//#region ENVIAR RESULTADO DA ANÁLISE (upload do arquivo do laboratório)
interface ParamsEnviarResultado {
    /** cod da OS que recebe o resultado */
    cod: number
    /** conteúdo do arquivo (.csv/.txt) em base64 */
    base64: string
    refetch?: Function
}

export const useEnviarResultado = (): {
    carregando_enviar_resultado: boolean;
    mutate_enviar_resultado: (
        params: ParamsEnviarResultado,
        options?: MutateOptions<void, Error, ParamsEnviarResultado>
    ) => void;
} => {

    const mutation: UseMutationResult<void, Error, ParamsEnviarResultado> = useMutation(
        async (params: ParamsEnviarResultado) => {
            const { cod, base64, refetch } = params;

            loadingAlert({ msg: 'Enviando resultado...', id: 'resultado' });

            return putPedlaboratorioResultado(cod, base64)
                .then((response: any) => {
                    if (response.status !== 200) {
                        throw new Error('Falha ao enviar resultado!');
                    }
                    successAlert('Resultado enviado com sucesso!', 5000, 'resultado');
                    refetch?.();
                })
                .catch((error: any) => {
                    // O backend valida o conteúdo do arquivo e devolve os erros por campo;
                    // exibir cada um é o que permite ao usuário corrigir a planilha.
                    const erros = error?.response?.data?.error;

                    if (Array.isArray(erros) && erros[0]?.field) {
                        destroyAlert('resultado');
                        erros.forEach((item: any) => {
                            errorAlert(item?.msg?.[0] ?? 'Falha na operação', 5000);
                        });
                    } else if (erros) {
                        errorAlert(
                            typeof erros === 'string' ? erros : 'Falha na operação',
                            5000,
                            'resultado'
                        );
                    } else {
                        errorAlert('Falha ao enviar resultado!', 5000, 'resultado');
                    }

                    throw error;
                });
        }
    );

    return {
        carregando_enviar_resultado: mutation.isLoading,
        mutate_enviar_resultado: mutation.mutate,
    };
};
//#endregion

//#region REPROCESSAR OS
interface ParamsReprocessarOs {
    cod: number
    cod_cliente: number
    refetch?: Function
}

export const useReprocessarOs = (): {
    carregando_reprocessar: boolean;
    mutate_reprocessar: (
        params: ParamsReprocessarOs,
        options?: MutateOptions<void, Error, ParamsReprocessarOs>
    ) => void;
} => {

    const mutation: UseMutationResult<void, Error, ParamsReprocessarOs> = useMutation(
        async (params: ParamsReprocessarOs) => {
            const { cod, cod_cliente, refetch } = params;

            loadingAlert({ msg: 'Reprocessando OS...', id: 'proce_os' });

            return reprocessarOs(cod, cod_cliente)
                .then((response: any) => {
                    if (response.status !== 200) {
                        throw new Error('Falha ao reprocessar a OS.');
                    }
                    successAlert('OS reprocessada com sucesso!', 5000, 'proce_os');
                    refetch?.();
                })
                .catch((error: any) => {
                    errorAlert('Ocorreu um erro ao reprocessar a OS.', 5000, 'proce_os');
                    throw error;
                });
        }
    );

    return {
        carregando_reprocessar: mutation.isLoading,
        mutate_reprocessar: mutation.mutate,
    };
};
//#endregion
